import * as React from 'react';
import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Tooltip } from '@mui/material';
import { ChevronRight, ExpandMore } from '@mui/icons-material';
import { TFunction } from 'i18next';
import { useAppTranslation } from '../../services/i18n';
import { dateToGui } from '../../helpers/date';
import {
  DropInstruction,
  dropInstructionAt,
  leadershipOf,
  shortName,
  WorkplaceAssignment,
  WorkplaceRow,
} from '../../helpers/workplaceTree';
import { WorkplaceRowMenu } from './WorkplaceRowMenu';

export const WORKPLACE_DRAG_TYPE = 'workplace';

interface DragItem {
  id: number;
}

export const WORKPLACE_ROW_HEIGHT = 60;

interface Props {
  row: WorkplaceRow;
  top: number;
  isActive: boolean;
  isSelected: boolean;
  /** collapsing is meaningless while a filter forces the tree open */
  canToggle: boolean;
  /** dragging is off while a filter shows only a part of the structure */
  canDrag: boolean;
  /** false for a structure with no children anywhere -- then dragging only reorders */
  canNest: boolean;
  /** off draws the row flat: no indentation, no chevron, no nesting in the menu */
  isTreeEnabled: boolean;
  /** the unit being dragged, itself and its own subtree; nothing may be dropped on those */
  forbiddenIds: Set<number> | null;
  /** what the current drag would do to this row, if anything */
  dropInstruction: DropInstruction | null;
  onToggle: (id: number) => void;
  onSelect: (id: number) => void;
  onDragOver: (id: number, instruction: DropInstruction | null) => void;
  onDrop: (draggedId: number, targetId: number, instruction: DropInstruction) => void;
  /* the row menu offers what the keyboard already can, so these are the same handlers */
  onEdit: (id: number) => void;
  onCreateChild: (id: number) => void;
  onCreateSibling: (id: number) => void;
  onMoveTo: (id: number) => void;
  onReorder: (id: number, delta: -1 | 1) => void;
  onDelete: (id: number) => void;
}

/**
 * The API only ever returns assignments that are valid today, so both dates are read
 * as they stand -- no window has to be worked out here.
 */
const validityLabel = (t: TFunction, deputy: WorkplaceAssignment): string => {
  const from = dateToGui(deputy.valid_from);
  const to = dateToGui(deputy.valid_to);
  if (from && to) {
    return t('workplaces.row.between', { from, to });
  }
  if (to) {
    return t('workplaces.row.until', { date: to });
  }
  return from ? t('workplaces.row.since', { date: from }) : '';
};

/** the tooltip carries a titled block, the way the shortcuts one does */
const LeaderList: React.FC<{
  label: string;
  leaders: WorkplaceAssignment[];
  /** a stand-in covers the whole post, so every holder is struck through, not just the named one */
  isReplaced?: boolean;
}> = ({ label, leaders, isReplaced }) => (
  <div>
    <strong>{label}</strong>
    {leaders.map((item) => (
      <div key={item.id} className={isReplaced ? 'workplace-row__leader-name--replaced' : ''}>
        {item.full_name}
      </div>
    ))}
  </div>
);

const Leadership: React.FC<{ row: WorkplaceRow }> = ({ row }) => {
  const t = useAppTranslation();
  const { leaders, deputy } = leadershipOf(row.node.unit);
  const [first, ...rest] = leaders;
  const leaderLabel = t('workplaces.assignments.leader');

  if (!first && !deputy) {
    return (
      <span className={'workplace-row__leader workplace-row__leader--none'}>
        {t('workplaces.row.noLeader')}
      </span>
    );
  }

  return (
    <span className={'workplace-row__leader'}>
      {first && (
        // leaders who are currently being stood in for are struck through, so the row
        // says at a glance who is actually in charge today. Only the first one is named:
        // a row has to stay on one line, the rest are behind the tooltip.
        <Tooltip
          title={
            rest.length ? (
              <LeaderList label={leaderLabel} leaders={leaders} isReplaced={!!deputy} />
            ) : (
              ''
            )
          }
        >
          <span
            className={
              'workplace-row__leader-name' +
              (deputy ? ' workplace-row__leader-name--replaced' : '')
            }
          >
            {t('workplaces.row.leader', { name: shortName(first.full_name) })}
            {!!rest.length && t('workplaces.row.moreLeaders', { n: rest.length })}
          </span>
        </Tooltip>
      )}
      {deputy && (
        // only the stand-in themselves is coloured; the words joining them to the post
        // and the dates stay in body grey, the way the design draws the row
        <span className={'workplace-row__deputy'}>
          {first ? ' ' + t('workplaces.row.replacedBy') + ' ' : ' '}
          <span className={'workplace-row__deputy-name'}>
            {t('workplaces.row.deputy', { name: shortName(deputy.full_name) })}
          </span>
          {' ' + validityLabel(t, deputy)}
        </span>
      )}
    </span>
  );
};

export const WorkplaceTreeRow: React.FC<Props> = ({
  row,
  top,
  isActive,
  isSelected,
  canToggle,
  canDrag,
  canNest,
  isTreeEnabled,
  forbiddenIds,
  dropInstruction,
  onToggle,
  onSelect,
  onDragOver,
  onDrop,
  onEdit,
  onCreateChild,
  onCreateSibling,
  onMoveTo,
  onReorder,
  onDelete,
}) => {
  const t = useAppTranslation();
  const { node, level, hasChildren, isExpanded, isMatch } = row;
  const elementRef = useRef<HTMLDivElement>(null);
  // hover fires on every mouse move; the parent is told only when the answer changes
  const reported = useRef<DropInstruction | null>(null);

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    onToggle(node.id);
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: WORKPLACE_DRAG_TYPE,
      item: { id: node.id } as DragItem,
      canDrag,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [node.id, canDrag]
  );

  const instructionFor = (monitor: {
    getClientOffset: () => { y: number } | null;
  }): DropInstruction | null => {
    const rect = elementRef.current?.getBoundingClientRect();
    const offset = monitor.getClientOffset();
    if (!rect || !offset) {
      return null;
    }
    return dropInstructionAt(offset.y - rect.top, rect.height, canNest, reported.current || undefined);
  };

  const [, drop] = useDrop(
    () => ({
      accept: WORKPLACE_DRAG_TYPE,
      canDrop: () => !forbiddenIds?.has(node.id),
      hover: (item: DragItem, monitor) => {
        if (!monitor.canDrop()) {
          return;
        }
        const instruction = instructionFor(monitor);
        if (instruction && instruction !== reported.current) {
          reported.current = instruction;
          onDragOver(node.id, instruction);
        }
      },
      drop: (item: DragItem, monitor) => {
        const instruction = instructionFor(monitor) || reported.current;
        if (instruction) {
          onDrop(item.id, node.id, instruction);
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [node.id, canNest, forbiddenIds, onDragOver, onDrop]
  );

  if (dropInstruction === null && reported.current !== null) {
    reported.current = null;
  }

  drag(drop(elementRef));

  return (
    <div
      ref={elementRef}
      role={'treeitem'}
      id={`workplace-node-${node.id}`}
      data-workplace-id={node.id}
      aria-level={level + 1}
      aria-posinset={row.posInSet}
      aria-setsize={row.setSize}
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-selected={isSelected}
      tabIndex={isActive ? 0 : -1}
      className={
        'workplace-row' +
        (isSelected ? ' workplace-row--selected' : '') +
        (isActive ? ' workplace-row--active' : '') +
        (isMatch ? '' : ' workplace-row--context') +
        (isDragging ? ' workplace-row--dragging' : '') +
        (dropInstruction ? ' workplace-row--drop-' + dropInstruction : '')
      }
      data-drop={dropInstruction || undefined}
      style={{
        top,
        height: WORKPLACE_ROW_HEIGHT,
        paddingLeft: 12 + (isTreeEnabled ? level * 24 : 0),
      }}
      onClick={() => onSelect(node.id)}
    >
      <button
        type={'button'}
        tabIndex={-1}
        aria-hidden={true}
        className={'workplace-row__toggle'}
        disabled={!hasChildren || !canToggle}
        onClick={handleToggle}
      >
        {isTreeEnabled && hasChildren ? isExpanded ? <ExpandMore /> : <ChevronRight /> : null}
      </button>
      <span className={'workplace-row__body'}>
        <span className={'workplace-row__title'}>
          {node.unit.code && <span className={'workplace-row__code'}>{node.unit.code}</span>}
          <span className={'workplace-row__name'}>{node.unit.name}</span>
        </span>
        <Leadership row={row} />
      </span>
      <span
        className={'workplace-row__count'}
        title={
          node.totalUserCount !== node.userCount
            ? t('workplaces.row.usersTitle', {
                own: node.userCount,
                total: node.totalUserCount,
              })
            : undefined
        }
      >
        {/* memberships in this unit alone -- the number the filters work with */}
        {t('workplaces.row.users', { n: node.userCount })}
        {node.totalUserCount !== node.userCount && (
          <span className={'workplace-row__count-subtree'}>
            {' '}
            {t('workplaces.row.subtreeUsers', { n: node.totalUserCount - node.userCount })}
          </span>
        )}
      </span>
      <WorkplaceRowMenu
        name={node.unit.name}
        isTreeEnabled={isTreeEnabled}
        canMoveUp={row.posInSet > 1}
        canMoveDown={row.posInSet < row.setSize}
        onEdit={() => onEdit(node.id)}
        onCreateChild={() => onCreateChild(node.id)}
        onCreateSibling={() => onCreateSibling(node.id)}
        onMoveTo={() => onMoveTo(node.id)}
        onMoveUp={() => onReorder(node.id, -1)}
        onMoveDown={() => onReorder(node.id, 1)}
        onDelete={() => onDelete(node.id)}
      />
    </div>
  );
};
